import React, { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import SchoolModal from './parts/SchoolModal';
import RegisterSchoolAdminModal from './parts/RegisterSchoolAdminModal';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import CustomPaginator from '../../components/CustomPaginator';

// ── Types ─────────────────────────────────────────────────────────────────────

interface School {
  _id?: string;
  id?: string;
  name: string;
  state: string;
  district: string;
  pincode: string;
  school_id_code: string;
  school_type: string;
  status: string;
  created_at: string;
  updated_at?: string;
  has_admin?: boolean;
}

const getSchoolId = (school: School): string => school._id || school.id || '';

// ── Component ─────────────────────────────────────────────────────────────────

export default function SchoolLocationSetup() {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [allSchoolsCache, setAllSchoolsCache] = useState<School[]>([]);

  // ── School Modal controls ─────────────────────────────────────────────────
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);

  // ── Admin Modal controls ──────────────────────────────────────────────────
  const [adminModalVisible, setAdminModalVisible] = useState(false);
  const [adminTargetSchool, setAdminTargetSchool] = useState<School | null>(null);

  const toast = useRef<Toast>(null);

  // ── Data fetching ─────────────────────────────────────────────────────────

  useEffect(() => {
    fetchSchools();
  }, []);

  useEffect(() => {
    // Client-side pagination over the cached full list
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    setSchools(allSchoolsCache.slice(start, end));
  }, [page, pageSize, allSchoolsCache]);

  const fetchSchools = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/schools');
      const data = response.data;

      let schoolData: School[] = [];

      if (Array.isArray(data)) {
        schoolData = data;
      } else if (data?.status === 'success' && Array.isArray(data.data)) {
        schoolData = data.data;
      } else if (data?.schools && Array.isArray(data.schools)) {
        schoolData = data.schools;
      } else {
        console.warn('Unexpected response format:', data);
        schoolData = [];
      }

      setAllSchoolsCache(schoolData);
      setTotalRecords(schoolData.length);
      setPage(1);
    } catch (error: any) {
      console.error('Error fetching schools:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error?.response?.data?.error || 'Failed to fetch schools data.',
        life: 3000,
      });
      setAllSchoolsCache([]);
      setSchools([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = (): void => {
    fetchSchools();
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  // ── School Modal helpers ──────────────────────────────────────────────────

  const openCreate = () => {
    setSelectedSchool(null);
    setModalVisible(true);
  };

  const openEdit = (school: School) => {
    setSelectedSchool(school);
    setModalVisible(true);
  };

  const handleModalSuccess = () => {
    setModalVisible(false);
    setSelectedSchool(null);
    refreshData();
  };

  // ── Admin Modal helpers ───────────────────────────────────────────────────

  const openRegisterAdmin = (school: School) => {
    if (school.has_admin) return;
    setAdminTargetSchool(school);
    setAdminModalVisible(true);
  };

  const handleAdminModalClose = () => {
    setAdminModalVisible(false);
    setAdminTargetSchool(null);
  };

  const handleAdminSuccess = () => {
    setAdminModalVisible(false);
    setAdminTargetSchool(null);
    refreshData();
  };

  // ── Delete handler ────────────────────────────────────────────────────────

  const handleDelete = async (school: School) => {
    const schoolId = getSchoolId(school);
    if (!schoolId) return;

    const confirmed = window.confirm(`Are you sure you want to delete "${school.name}"?`);
    if (!confirmed) return;

    try {
      await apiClient.delete(`/api/schools/${schoolId}`);
      toast.current?.show({
        severity: 'success',
        summary: 'Deleted',
        detail: `"${school.name}" has been removed.`,
        life: 3000,
      });
      refreshData();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error?.response?.data?.error || 'Failed to delete school.',
        life: 3000,
      });
    }
  };

  // ── Column templates ──────────────────────────────────────────────────────

  const codeBodyTemplate = (rowData: School) => (
    <span
      style={{
        fontFamily: 'monospace',
        fontSize: 12,
        background: '#f9fafb',
        padding: '4px 8px',
        borderRadius: 6,
        fontWeight: 700,
        color: '#1f2937',
        border: '1px solid #e5e7eb',
        display: 'inline-block',
      }}
    >
      {rowData.school_id_code || '—'}
    </span>
  );

  const nameBodyTemplate = (r: School) => (
    <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 600 }}>{r.name || '—'}</div>
  );

  const stateBodyTemplate = (r: School) => (
    <div style={{ fontSize: 13, color: '#374151', fontWeight: 500, textTransform: 'capitalize' }}>
      {r.state?.replace(/-/g, ' ') || '—'}
    </div>
  );

  const districtBodyTemplate = (r: School) => (
    <div style={{ fontSize: 13, color: '#374151', fontWeight: 500, textTransform: 'capitalize' }}>
      {r.district?.replace(/-/g, ' ') || '—'}
    </div>
  );

  const pincodeBodyTemplate = (r: School) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, fontFamily: 'monospace' }}>
      {r.pincode || '—'}
    </div>
  );

  const schoolTypeBodyTemplate = (rowData: School) => {
    const colorMap: Record<string, { bg: string; color: string }> = {
      private: { bg: '#dbeafe', color: '#1d4ed8' },
      public: { bg: '#dcfce7', color: '#16a34a' },
      international: { bg: '#fef3c7', color: '#d97706' },
    };
    const style = colorMap[rowData.school_type?.toLowerCase()] ?? { bg: '#f3f4f6', color: '#6b7280' };
    return (
      <span
        style={{
          background: style.bg,
          color: style.color,
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'capitalize',
          display: 'inline-block',
        }}
      >
        {rowData.school_type || '—'}
      </span>
    );
  };

  const statusBodyTemplate = (rowData: School) => {
    const colorMap: Record<string, { bg: string; color: string }> = {
      active: { bg: '#dcfce7', color: '#16a34a' },
      inactive: { bg: '#fee2e2', color: '#991b1b' },
      suspended: { bg: '#fef3c7', color: '#d97706' },
      pending: { bg: '#dbeafe', color: '#1e40af' },
    };
    const style = colorMap[rowData.status?.toLowerCase()] ?? { bg: '#f3f4f6', color: '#6b7280' };
    return (
      <span
        style={{
          background: style.bg,
          color: style.color,
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'capitalize',
          display: 'inline-block',
        }}
      >
        {rowData.status || '—'}
      </span>
    );
  };

  const adminStatusBodyTemplate = (rowData: School) => {
    if (rowData.has_admin) {
      return (
        <span
          style={{
            background: '#dcfce7',
            color: '#16a34a',
            padding: '4px 10px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <i className="pi pi-check-circle" style={{ fontSize: 10 }} />
          Assigned
        </span>
      );
    }
    return (
      <span
        style={{
          background: '#fee2e2',
          color: '#991b1b',
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <i className="pi pi-exclamation-circle" style={{ fontSize: 10 }} />
        Not Assigned
      </span>
    );
  };

  const dateBodyTemplate = (r: School) => {
    if (!r.created_at) return <span style={{ color: '#9ca3af' }}>—</span>;
    return (
      <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
        {new Date(r.created_at).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}
      </div>
    );
  };

  const actionsBodyTemplate = (rowData: School) => (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
      <Button
        icon={rowData.has_admin ? 'pi pi-check' : 'pi pi-user-plus'}
        className="p-button-text p-button-rounded"
        tooltip={rowData.has_admin ? 'Admin already registered' : 'Register Admin'}
        tooltipOptions={{ position: 'top' }}
        disabled={rowData.has_admin}
        style={{
          color: rowData.has_admin ? '#9ca3af' : '#8b5cf6',
          width: 32,
          height: 32,
        }}
        onClick={() => openRegisterAdmin(rowData)}
      />
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded"
        tooltip="Edit School"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#3b82f6', width: 32, height: 32 }}
        onClick={() => openEdit(rowData)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded"
        tooltip="Delete School"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#ef4444', width: 32, height: 32 }}
        onClick={() => handleDelete(rowData)}
      />
    </div>
  );

  // ── Stat cards (computed from full cache so counts are accurate) ──────────

  const statCards = [
    {
      label: 'Total Schools',
      value: totalRecords,
      icon: 'pi-building',
      tint: 'rgba(59,130,246,.12)',
      iconColor: '#2563eb',
    },
    {
      label: 'Public Schools',
      value: allSchoolsCache.filter((s) => s.school_type?.toLowerCase() === 'public').length,
      icon: 'pi-check-circle',
      tint: 'rgba(16,185,129,.12)',
      iconColor: '#059669',
    },
    {
      label: 'Private Schools',
      value: allSchoolsCache.filter((s) => s.school_type?.toLowerCase() === 'private').length,
      icon: 'pi-briefcase',
      tint: 'rgba(59,130,246,.12)',
      iconColor: '#2563eb',
    },
    {
      label: 'International',
      value: allSchoolsCache.filter((s) => s.school_type?.toLowerCase() === 'international').length,
      icon: 'pi-globe',
      tint: 'rgba(245,158,11,.12)',
      iconColor: '#d97706',
    },
    {
      label: 'Active Schools',
      value: allSchoolsCache.filter((s) => s.status?.toLowerCase() === 'active').length,
      icon: 'pi-check',
      tint: 'rgba(139,92,246,.12)',
      iconColor: '#7c3aed',
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif', background: '#f8fafc' }}>
        <style>{`
          @keyframes slideIn { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }
          @keyframes fadeInSlide { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:translateY(0) } }
          .p-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
          .p-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 #4169E1; }
          .p-button-text:hover { background:#f3f4f6!important; }
          .p-datatable .p-datatable-thead > tr > th {
            background:#f9fafb!important;
            color:#6b7280!important;
            font-size:11px!important;
            font-weight:700!important;
            text-transform:uppercase!important;
            letter-spacing:.8px!important;
            padding:16px 12px!important;
            border-bottom:1px solid #e5e7eb!important;
          }
          .p-datatable .p-datatable-tbody > tr > td {
            padding:16px 12px!important;
            border-bottom:1px solid #f3f4f6!important;
          }
          .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
          .p-datatable .p-datatable-emptymessage td { padding:0!important; }
          .header-section { animation: fadeInSlide .5s ease-out; }
          .table-section { animation: fadeInSlide .55s ease-out; }
        `}</style>

        {/* ── Header (kept as-is) ── */}
        <div
          className="header-section"
          style={{
            background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
            borderRadius: 24,
            padding: 32,
            marginBottom: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 8px 32px rgba(40,85,174,0.15)',
            flexWrap: 'wrap',
            gap: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                background: 'rgba(255,255,255,0.25)',
                borderRadius: 16,
                padding: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              <i className="pi pi-building" style={{ fontSize: 24, color: 'white' }} />
            </div>
            <div>
              <h1
                style={{
                  color: 'white',
                  fontSize: 28,
                  fontWeight: 700,
                  margin: '0 0 6px 0',
                  letterSpacing: '-0.5px',
                }}
              >
                School Management
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span
                  style={{
                    background: 'rgba(16,185,129,0.95)',
                    color: 'white',
                    padding: '6px 14px',
                    borderRadius: 14,
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {totalRecords} Schools
                </span>
                <span style={{ color: 'rgba(255,255,255,0.95)', fontSize: 14, fontWeight: 500 }}>
                  Manage school locations and configurations
                </span>
              </div>
            </div>
          </div>
          <Button
            label="Add School"
            icon="pi pi-plus"
            onClick={openCreate}
            style={{
              background: 'white',
              color: '#2855ae',
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              fontSize: 15,
              fontWeight: 600,
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          />
        </div>

        {/* ── Stat Cards ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 16,
            marginBottom: 24,
          }}
        >
          {statCards.map((card) => (
            <div
              key={card.label}
              style={{
                background: 'white',
                borderRadius: 16,
                padding: 20,
                boxShadow: '0 2px 12px rgba(0,0,0,.06)',
                border: '1px solid #e5e7eb',
                transition: 'all .2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,.06)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: card.tint,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className={`pi ${card.icon}`} style={{ fontSize: 16, color: card.iconColor }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>{card.label}</div>
                  <div style={{ fontSize: 24, color: '#1f2937', fontWeight: 700, lineHeight: 1.2 }}>
                    {card.value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Data Table ── */}
        <div
          className="table-section"
          style={{
            background: 'white',
            borderRadius: 20,
            boxShadow: '0 2px 12px rgba(0,0,0,.06)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            marginBottom: 24,
          }}
        >
          {/* Table header bar */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #f3f4f6',
              background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-list" style={{ fontSize: 14, color: '#4169E1' }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>Schools Directory</div>
                <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                  {totalRecords > 0
                    ? `Showing ${Math.min((page - 1) * pageSize + 1, totalRecords)}–${Math.min(page * pageSize, totalRecords)} of ${totalRecords} schools`
                    : 'View and manage all registered schools'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={refreshData}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: 'white',
                  color: '#4169E1',
                  border: '1.5px solid #c7d2fe',
                  transition: 'all .2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#eff6ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                <i className="pi pi-refresh" style={{ fontSize: 12 }} />
                Refresh
              </button>
              {totalPages > 0 && totalRecords > 0 && (
                <div
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                    border: '1px solid rgba(65,105,225,.15)',
                  }}
                >
                  <span style={{ fontSize: 13, color: '#4169E1', fontWeight: 600 }}>
                    Page {page} of {totalPages}
                  </span>
                </div>
              )}
            </div>
          </div>

          <DataTable
            value={schools}
            loading={loading}
            dataKey="_id"
            emptyMessage={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '60px 20px',
                  minHeight: 280,
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                    borderRadius: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 20,
                    boxShadow: '0 6px 18px rgba(59,130,246,.2)',
                  }}
                >
                  <i className="pi pi-building" style={{ fontSize: 25, color: 'white' }} />
                </div>
                <h3
                  style={{
                    margin: 0,
                    color: '#1e40af',
                    fontSize: 18,
                    fontWeight: 700,
                    marginBottom: 8,
                    textAlign: 'center',
                  }}
                >
                  No Schools Found
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: '#64748b',
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: 'center',
                    maxWidth: 360,
                    lineHeight: 1.6,
                  }}
                >
                  Add a school to get started with location and admin setup.
                </p>
              </div>
            }
          >
            <Column header="Code" body={codeBodyTemplate} style={{ width: 100 }} />
            <Column header="School Name" body={nameBodyTemplate} />
            <Column header="State" body={stateBodyTemplate} style={{ width: 120 }} />
            <Column header="District" body={districtBodyTemplate} style={{ width: 120 }} />
            <Column header="Pincode" body={pincodeBodyTemplate} style={{ width: 100 }} />
            <Column header="Type" body={schoolTypeBodyTemplate} style={{ width: 110 }} />
            <Column header="Status" body={statusBodyTemplate} style={{ width: 100 }} />
            <Column header="Admin" body={adminStatusBodyTemplate} style={{ width: 130 }} />
            <Column header="Created" body={dateBodyTemplate} style={{ width: 110 }} />
            <Column header="Actions" body={actionsBodyTemplate} style={{ width: 130 }} />
          </DataTable>

          {totalPages > 1 && (
            <CustomPaginator
              page={page}
              totalPages={totalPages}
              totalRecords={totalRecords}
              pageSize={pageSize}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
              showInfoBar={false}
            />
          )}
        </div>
      </div>

      {/* ── School Modal ── */}
      <SchoolModal
        visible={modalVisible}
        onHide={() => {
          setModalVisible(false);
          setSelectedSchool(null);
        }}
        onSuccess={handleModalSuccess}
        school={selectedSchool}
        toast={toast}
      />

      {/* ── Register Admin Modal ── */}
      {adminTargetSchool && (
        <RegisterSchoolAdminModal
          visible={adminModalVisible}
          onHide={handleAdminModalClose}
          onSuccess={handleAdminSuccess}
          schoolIdCode={adminTargetSchool.school_id_code}
          schoolName={adminTargetSchool.name}
          toast={toast}
        />
      )}
    </>
  );
}